import { useState } from "react";
import { Button } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { PageItem } from "./PageItem";
import type { Page } from "../../../shared/schema";

interface PageListProps {
  pages: Page[];
  selectedPageId: string | null;
  onSelectPage: (pageId: string | null) => void;
  onAddPage: () => void;
  onRemovePage: (pageId: string) => void;
  onReorderPages: (pageIds: string[]) => void;
}

// Helper to reorder array elements
function arrayMove<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...array];
  const removed = result.splice(fromIndex, 1)[0];
  if (removed !== undefined) {
    result.splice(toIndex, 0, removed);
  }
  return result;
}

export const PageList: React.FC<PageListProps> = ({
  pages,
  selectedPageId,
  onSelectPage,
  onAddPage,
  onRemovePage,
  onReorderPages,
}) => {
  const { t } = useTranslation();

  // Track which page is being dragged
  const [draggedPageId, setDraggedPageId] = useState<string | null>(null);

  const handleDragStart = (pageId: string) => {
    setDraggedPageId(pageId);
  };

  const handleDragOver = (e: React.DragEvent, targetPageId: string) => {
    e.preventDefault();
    if (!draggedPageId || draggedPageId === targetPageId) return;

    const fromIndex = pages.findIndex((p) => p.id === draggedPageId);
    const toIndex = pages.findIndex((p) => p.id === targetPageId);
    if (fromIndex === -1 || toIndex === -1) return;

    // Reorder immediately for smooth feedback
    const newOrder = arrayMove(pages, fromIndex, toIndex);
    onReorderPages(newOrder.map((p) => p.id));
  };

  const handleDragEnd = () => {
    setDraggedPageId(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">{t("Pages")}</h3>
        <Button variant="ghost" size="sm" onClick={onAddPage}>
          <Plus className="h-4 w-4 mr-1" />
          {t("Add")}
        </Button>
      </div>

      <div className="space-y-1">
        {pages.map((page, index) => (
          <PageItem
            key={page.id}
            page={page}
            isFirst={index === 0}
            isSelected={selectedPageId === page.id}
            isDragging={draggedPageId === page.id}
            canDelete={pages.length > 1}
            onSelect={() => onSelectPage(selectedPageId === page.id ? null : page.id)}
            onRemove={() => onRemovePage(page.id)}
            onDragStart={() => handleDragStart(page.id)}
            onDragOver={(e) => handleDragOver(e, page.id)}
            onDragEnd={handleDragEnd}
          />
        ))}
      </div>

      {pages.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">{t("No pages yet. Add one to get started.")}</p>
      )}
    </div>
  );
};
